export interface AvatarVideoPoint {
  x: number;
  y: number;
}

export interface AvatarVideoViewport {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

export interface AvatarVideoVisibilityInput {
  listener: AvatarVideoPoint | undefined;
  source: AvatarVideoPoint | undefined;
  previouslyVisible: boolean;
  hasVideo: boolean;
  connected: boolean;
  blocked: boolean;
  inViewport: boolean;
  enterRadius: number;
  exitRadius: number;
}

export function shouldUseAvatarVideoOverlays(
  featureEnabled: boolean,
  roomSpatialAudioActive: boolean,
): boolean {
  return featureEnabled && roomSpatialAudioActive;
}

export function extractUserIdFromSpaceUserId(spaceUserId: string): number | undefined {
  const match = /(?:^|_)(-?\d+)$/.exec(spaceUserId);
  if (!match) return undefined;

  const userId = Number(match[1]);
  return Number.isFinite(userId) ? userId : undefined;
}

export function isPointInViewport(
  point: AvatarVideoPoint,
  viewport: AvatarVideoViewport,
  margin: number,
): boolean {
  return (
    point.x >= viewport.left - margin &&
    point.x <= viewport.right + margin &&
    point.y >= viewport.top - margin &&
    point.y <= viewport.bottom + margin
  );
}

export function filterAvatarCameraEntries<T>(
  entries: Iterable<readonly [string, T]>,
  remoteCameraIds: Iterable<string>,
  localCameraId = "-1",
): Map<string, T> {
  const cameraIds = new Set(remoteCameraIds);
  cameraIds.add(localCameraId);
  return new Map(Array.from(entries).filter(([id]) => !cameraIds.has(id)));
}

export function resolveAvatarVideoVisibility(input: AvatarVideoVisibilityInput): boolean {
  if (
    !input.listener ||
    !input.source ||
    !input.hasVideo ||
    !input.connected ||
    input.blocked ||
    !input.inViewport
  ) {
    return false;
  }

  const enterRadius = Math.max(0, input.enterRadius);
  const exitRadius = Math.max(enterRadius, input.exitRadius);
  const radius = input.previouslyVisible ? exitRadius : enterRadius;
  const deltaX = input.source.x - input.listener.x;
  const deltaY = input.source.y - input.listener.y;

  return deltaX * deltaX + deltaY * deltaY <= radius * radius;
}
